import { createGlobalState } from '@vueuse/core';
import { Component, getCurrentInstance, shallowRef } from 'vue';
import { getInfoRead, postInfoRead } from '@/services/UserInfoRead';
import { logWarning } from '@/utils/logger';
import { UserRole } from '@/models/User';

const infoPopupComponents = {
  InfoPopup_ProjectQuestions: () => import('@/components/info-popups/InfoPopup_ProjectQuestions.vue'),
  InfoPopup_Cheatsheet: () => import('@/components/info-popups/InfoPopup_Cheatsheet.vue'),
};

export type InfoPopupComponentKey = keyof typeof infoPopupComponents;

const infoPopupUserRoles = [
  'FREELANCER',
  'COMPANY_ROOT',
  'COMPANY_GUEST',
  'COMPANY_EMPLOYEE',
] as const;

export type InfoPopupUserRole = typeof infoPopupUserRoles[number];
export type InfoPopupRoleVisibility = Partial<Record<InfoPopupUserRole, boolean>>;

export interface InfoReadRequest {
  context: string;
  value: string;
  visibleForRoles?: InfoPopupRoleVisibility;
}

export interface InfoPopupRequest extends InfoReadRequest {
  component: InfoPopupComponentKey;
}

export interface DirectInfoPopupRequest {
  component: InfoPopupComponentKey;
  visibleForRoles?: InfoPopupRoleVisibility;
}

type ComponentLoader = () => Promise<{ default: Component }>;

export interface InfoPopupStateDependencies {
  getInfoRead: typeof getInfoRead;
  postInfoRead: typeof postInfoRead;
  getCurrentUserRoles: () => UserRole[];
  components: Record<InfoPopupComponentKey, ComponentLoader>;
  warn: (...params: any[]) => void;
}

const defaultDependencies: InfoPopupStateDependencies = {
  getInfoRead,
  postInfoRead,
  getCurrentUserRoles: () => [],
  components: infoPopupComponents,
  warn: logWarning,
};

export const createInfoPopupState = (dependencies = defaultDependencies) => {
  const infoPopupComponent = shallowRef<Component | null>(null);
  const readValues = new Map<string, Set<string>>();
  let activePopup: InfoPopupRequest | null = null;
  let invocationId = 0;
  let cacheGeneration = 0;

  const addReadValue = (context: string, value: string) => {
    const values = readValues.get(context) ?? new Set<string>();
    values.add(value);
    readValues.set(context, values);
  };

  const isRead = ({ context, value }: InfoReadRequest) => (
    readValues.get(context)?.has(value) ?? false
  );

  const isVisibleForCurrentUser = ({
    visibleForRoles,
  }: Pick<InfoReadRequest, 'visibleForRoles'>) => {
    const currentUserRoles = dependencies.getCurrentUserRoles();
    return infoPopupUserRoles.some((role) => (
      visibleForRoles?.[role] === true && currentUserRoles.includes(role)
    ));
  };

  const hideInfoPopup = () => {
    invocationId += 1;
    activePopup = null;
    infoPopupComponent.value = null;
  };

  const resetInfoPopupState = () => {
    hideInfoPopup();
    cacheGeneration += 1;
    readValues.clear();
  };

  const shouldShowInfoIfUnread = async (
    request: InfoReadRequest,
    isCurrent: () => boolean = () => true,
  ): Promise<boolean> => {
    if (!isVisibleForCurrentUser(request) || isRead(request)) return false;
    const currentCacheGeneration = cacheGeneration;

    try {
      const backendValues = await dependencies.getInfoRead(request.context);
      if (!Array.isArray(backendValues)) {
        throw new TypeError('Invalid info-read response');
      }
      if (currentCacheGeneration !== cacheGeneration || !isCurrent()) return false;

      backendValues.forEach((item) => addReadValue(request.context, item.value));
      return !isRead(request);
    } catch (error) {
      dependencies.warn(`Could not load popup state: ${request.context}`, error);
      return false;
    }
  };

  const markInfoRead = async (request: InfoReadRequest): Promise<boolean> => {
    const currentCacheGeneration = cacheGeneration;

    try {
      const succeeded = await dependencies.postInfoRead(request.context, request.value);
      if (succeeded && currentCacheGeneration === cacheGeneration) {
        addReadValue(request.context, request.value);
      }
      return succeeded;
    } catch (error) {
      dependencies.warn(`Could not mark popup read: ${request.context}`, error);
      return false;
    }
  };

  const showInfoPopupIfUnread = async (request: InfoPopupRequest): Promise<boolean> => {
    hideInfoPopup();
    if (!isVisibleForCurrentUser(request)) return false;
    const currentInvocationId = invocationId;
    const loader = dependencies.components[request.component];

    if (!loader) {
      dependencies.warn(`No popup component registered for key: ${request.component}`);
      return false;
    }
    if (!await shouldShowInfoIfUnread(
      request,
      () => currentInvocationId === invocationId,
    )) return false;

    try {
      const loadedComponent = await loader();
      if (currentInvocationId !== invocationId) return false;
      activePopup = { ...request };
      infoPopupComponent.value = loadedComponent.default;
      return true;
    } catch (error) {
      dependencies.warn(`Could not load popup component: ${request.component}`, error);
      return false;
    }
  };

  const showInfoPopup = async (request: DirectInfoPopupRequest): Promise<boolean> => {
    hideInfoPopup();
    if (!isVisibleForCurrentUser(request)) return false;
    const currentInvocationId = invocationId;
    const loader = dependencies.components[request.component];

    if (!loader) {
      dependencies.warn(`No popup component registered for key: ${request.component}`);
      return false;
    }

    try {
      const loadedComponent = await loader();
      if (currentInvocationId !== invocationId) return false;
      activePopup = null;
      infoPopupComponent.value = loadedComponent.default;
      return true;
    } catch (error) {
      dependencies.warn(`Could not load popup component: ${request.component}`, error);
      return false;
    }
  };

  const markRead = async (): Promise<boolean | null> => {
    const popupToMark = activePopup;
    if (!popupToMark) {
      hideInfoPopup();
      return null;
    }
    hideInfoPopup();
    return markInfoRead(popupToMark);
  };

  return {
    infoPopupComponent,
    showInfoPopup,
    showInfoPopupIfUnread,
    hideInfoPopup,
    resetInfoPopupState,
    shouldShowInfoIfUnread,
    markInfoRead,
    markRead,
  };
};

const useInfoPopupState = createGlobalState(() => {
  const store = getCurrentInstance()?.proxy.$store;
  return createInfoPopupState({
    ...defaultDependencies,
    getCurrentUserRoles: () => store?.getters.user?.roles ?? [],
  });
});

export default useInfoPopupState;
